import type { ConfigKeys } from '@moment/configs-data-access'
import type { ConfigKey } from '@moment/horizon-data-access/config-keys'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import type { PageConfigResponse } from '@/types/api/page-config'
import type { PageConfig } from '@/types/schema/page-configs'

import { toError } from '@moment/observability/logger'

import { createLogger } from '@/lib/logger'
import { Page } from '@/types/schema/page-configs'
import { networkLogger } from '@/utils/logger/network-logger'

const LOGGER = createLogger('legacy-api/page-config-v2')

let configsV2Enabled = false

export function setPageConfigsV2Enabled(enabled: boolean): void {
  configsV2Enabled = enabled
}

export function isPageConfigsV2Enabled(): boolean {
  return configsV2Enabled
}

const PAGE_CONFIG_V2_VERSION = '1.0'

// Loaded on first V2 use so the store's import graph, which many tests build
// under partial horizon mocks, never evaluates the configs package.
const loadConfigsDataAccess = () => import('@moment/configs-data-access')
const loadPageConfigDefaults = () => import('@/reducers/defaults/page-configs')

const PAGE_CONFIG_KEYS: Partial<
  Record<Page, (keys: typeof ConfigKeys) => ConfigKey<object>>
> = {
  [Page.UserProfile]: (keys) => keys.userProfile.preferences,
  [Page.TradeBlotter]: (keys) => keys.pageView.tradeBlotter,
  [Page.TradeDeskTradeBlotter]: (keys) => keys.pageView.tradeDeskTradeBlotter,
  [Page.AccountList]: (keys) => keys.pageView.accountList,
  [Page.HoldingList]: (keys) => keys.pageView.holdingList,
  [Page.ModelList]: (keys) => keys.pageView.modelList,
  [Page.AllocationList]: (keys) => keys.pageView.allocationList,
  [Page.Proposals]: (keys) => keys.pageView.proposals,
}

const EMPTY_CONFIG_PAGES = new Set<string>([
  Page.FilterList,
  Page.HistoricalOrders,
])

const FIELD_NAMES: Record<string, string> = {
  filterPaneState: 'filter_pane_state',
  rowClickBehavior: 'row_click_behavior',
  quickPreviewLocation: 'quick_preview_location',
  viewType: 'view_type',
  identifierPreference: 'identifier_preference',
  separateProposalPerAccount: 'separate_proposal_per_account',
  pinnedViewFolderIds: 'pinned_view_folder_ids',
}

const COLUMN_FIELD_NAMES: Record<string, string> = {
  colId: 'col_id',
  displayName: 'display_name',
  visible: 'visible',
  width: 'width',
}

const invert = (names: Record<string, string>) =>
  Object.fromEntries(Object.entries(names).map(([from, to]) => [to, from]))

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

function renameFields(
  source: unknown,
  names: Record<string, string>,
): Record<string, unknown> {
  const renamed: Record<string, unknown> = {}
  if (!isRecord(source)) return renamed
  for (const [from, to] of Object.entries(names)) {
    const value = source[from]
    if (value !== undefined && value !== null) renamed[to] = value
  }
  return renamed
}

function renameColumnState(
  columnState: unknown,
  names: Record<string, string>,
): unknown {
  const renameColumns = (columns: unknown) =>
    Array.isArray(columns)
      ? columns.map((column) => renameFields(column, names))
      : columns
  if (Array.isArray(columnState)) return renameColumns(columnState)
  if (!isRecord(columnState)) return columnState
  return Object.fromEntries(
    Object.entries(columnState)
      .filter(([, columns]) => columns !== undefined && columns !== null)
      .map(([group, columns]) => [group, renameColumns(columns)]),
  )
}

function convert(
  source: object,
  names: Record<string, string>,
  columnNames: Record<string, string>,
  columnStateFrom: string,
  columnStateTo: string,
): Record<string, unknown> {
  const converted = renameFields(source, names)
  const columnState = (source as Record<string, unknown>)[columnStateFrom]
  if (columnState !== undefined && columnState !== null) {
    converted[columnStateTo] = renameColumnState(columnState, columnNames)
  }
  return converted
}

export function toConfigsV2Value(config: object): Record<string, unknown> {
  return convert(
    config,
    FIELD_NAMES,
    COLUMN_FIELD_NAMES,
    'columnState',
    'column_state',
  )
}

/** Converts stored V2 prefs onto `defaults`, so fields the stored value lacks keep their default. */
export function fromConfigsV2Value(
  value: object,
  defaults: PageConfig,
): PageConfig {
  const stored = convert(
    value,
    invert(FIELD_NAMES),
    invert(COLUMN_FIELD_NAMES),
    'column_state',
    'columnState',
  )
  const base = defaults as Record<string, unknown>
  const merged = { ...base, ...stored }
  if (isRecord(base.columnState) && isRecord(stored.columnState)) {
    merged.columnState = { ...base.columnState, ...stored.columnState }
  }
  return merged as PageConfig
}

export function hasConfigsV2Source(id: string): boolean {
  return id in PAGE_CONFIG_KEYS || EMPTY_CONFIG_PAGES.has(id)
}

function failed(
  method: 'GET' | 'PUT',
  id: string,
  error: unknown,
  startTimestamp: number | undefined,
): { error: FetchBaseQueryError } {
  if (startTimestamp !== undefined) {
    networkLogger.error(method, configsV2Url(id), error, startTimestamp)
  }
  LOGGER.error('Configs V2 page-config request failed', {
    error: toError(error),
    method,
    pageConfigId: id,
  })
  return { error: { status: 'CUSTOM_ERROR', error: toError(error).message } }
}

const configsV2Url = (id: string) => `configs-v2/page-config/${id}`

export async function readPageConfigV2(
  id: string,
): Promise<{ data: PageConfigResponse } | { error: FetchBaseQueryError }> {
  const page = id as Page
  const response = (config: PageConfig) => ({
    data: { id: page, version: PAGE_CONFIG_V2_VERSION, config },
  })
  const selectKey = PAGE_CONFIG_KEYS[page]
  if (selectKey === undefined) return response({})
  const startTimestamp = networkLogger.request('GET', configsV2Url(id))
  try {
    const [{ ConfigKeys, fetchConfig }, { defaultPageConfigs }] =
      await Promise.all([loadConfigsDataAccess(), loadPageConfigDefaults()])
    const value = await fetchConfig(selectKey(ConfigKeys))
    if (value === undefined) return { error: { status: 404, data: null } }
    return response(fromConfigsV2Value(value, defaultPageConfigs[page]))
  } catch (error) {
    return failed('GET', id, error, startTimestamp)
  }
}

export async function writePageConfigV2(
  id: string,
  config: PageConfig,
): Promise<{ data: null } | { error: FetchBaseQueryError }> {
  const selectKey = PAGE_CONFIG_KEYS[id as Page]
  if (selectKey === undefined) return { data: null }
  const startTimestamp = networkLogger.request('PUT', configsV2Url(id), config)
  try {
    const { ConfigKeys, saveUserConfig } = await loadConfigsDataAccess()
    await saveUserConfig(selectKey(ConfigKeys), toConfigsV2Value(config))
    return { data: null }
  } catch (error) {
    return failed('PUT', id, error, startTimestamp)
  }
}
